import React, { useEffect, useRef, useState } from "react";
import { Lock, Share, X } from "lucide-react";
import { shareText } from "../capacitor/share";
import { PRIMARY_BUTTON } from "../games/GameParts";
import { Helmet } from "../games/GameParts";
import art from "../games/pickThreeArt";

// The pieces the Pick Three card and page share: the backdrop, the three slots
// and the points chip.

const NAVY = "#071F39";

/**
 * A navy surface, with the stadium photo under a dark scrim when one is set
 * (games/pickThreeArt.js). The scrim keeps white text readable on any photo.
 * Children sit above it.
 */
export function PickThreeBackdrop({ children, style }) {
  const background = art
    ? `linear-gradient(180deg, rgba(7,31,57,0.72) 0%, rgba(7,31,57,0.9) 100%), url(${art}) center / cover`
    : `linear-gradient(135deg, ${NAVY} 0%, #0a3a6b 100%)`;
  return <div style={{ background, color: "#fff", ...style }}>{children}</div>;
}

/** "+9", "-3", "0". */
export const signed = (points) => (points > 0 ? `+${points}` : String(points));

const toneOf = (points) => (points > 0 ? "#7dffa3" : points < 0 ? "#ff9b94" : "#fff");

const CAPTAIN = "#FFCC00"; // --color-captain, on the navy surface

/**
 * Three slots. `picks` are slugs; an empty slot shows a plus.
 *
 * `scores` lines up with `picks`: a scoreEntry row puts what that pick adds
 * under the slot (double for the captain; a dash while the game has not been
 * scored), and a missing entry shows nothing, which is how a picker shows
 * points only for picks whose games have started.
 *
 * `captain` gets a C badge. With `onCaptain`, tapping a slot makes it captain
 * when `canCaptain(slug)` allows. With `onRemove`, an unlocked slot has a small
 * remove button. A slug in `locked` shows a lock and offers neither.
 */
export function Slots({
  picks, teams, scores = null, compact = false, locked = [], captain = null, onCaptain, canCaptain = () => true, onRemove,
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 8 }}>
      {Array.from({ length: 3 }, (_, i) => {
        const slug = picks[i];
        const team = slug ? teams?.[slug] : null;
        const score = scores?.[i];
        const isLocked = Boolean(slug) && locked.includes(slug);
        const isCaptain = Boolean(slug) && slug === captain;
        const box = {
          width: "100%", height: "100%", minHeight: compact ? 64 : 88, padding: compact ? "6px 4px" : "8px 4px", borderRadius: 12,
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4, color: "#fff",
          minWidth: 0, boxSizing: "border-box", font: "inherit",
          border: isCaptain ? `2px solid ${CAPTAIN}` : team ? "1.5px solid rgba(255,255,255,0.35)" : "1.5px dashed rgba(255,255,255,0.4)",
          backgroundColor: team ? "rgba(255,255,255,0.12)" : "transparent",
        };

        if (!team) {
          return (
            <div key={i} style={box}>
              <span style={{ fontSize: compact ? 18 : 22, fontWeight: 300, color: "rgba(255,255,255,0.7)" }}>+</span>
            </div>
          );
        }

        const content = (
          <>
            <Helmet team={team} size={compact ? 24 : 34} />
            <span style={{ fontSize: compact ? 11 : 12, fontWeight: 700, lineHeight: 1.2, textAlign: "center", overflowWrap: "anywhere" }}>{team.name}</span>
            {score && (
              <span style={{ fontSize: compact ? 15 : 18, fontWeight: 800, color: score.scored == null ? "rgba(255,255,255,0.6)" : toneOf(score.scored), fontVariantNumeric: "tabular-nums" }}>
                {score.scored == null ? "\u2014" : signed(score.scored)}
              </span>
            )}
          </>
        );
        const tappable = Boolean(onCaptain) && canCaptain(slug);
        return (
          <div key={i} style={{ position: "relative", minWidth: 0 }}>
            {tappable ? (
              <button type="button" onClick={() => onCaptain(slug)} aria-label={`Make ${team.name} captain`} style={{ ...box, cursor: "pointer" }}>
                {content}
              </button>
            ) : (
              <div style={box} aria-label={isCaptain ? `${team.name}, captain` : team.name}>{content}</div>
            )}
            {isCaptain && (
              <span
                aria-hidden="true"
                style={{
                  position: "absolute", top: 5, left: 5, width: 17, height: 17, borderRadius: "50%", backgroundColor: CAPTAIN,
                  color: NAVY, fontSize: 10, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                C
              </span>
            )}
            {isLocked ? (
              <span aria-label="Locked" style={{ position: "absolute", top: 6, right: 6, display: "flex", color: "rgba(255,255,255,0.75)" }}>
                <Lock size={11} strokeWidth={2.6} />
              </span>
            ) : onRemove && (
              <button
                type="button"
                onClick={() => onRemove(slug)}
                aria-label={`Remove ${team.name}`}
                style={{
                  position: "absolute", top: 2, right: 2, width: 24, height: 24, padding: 0, border: "none", borderRadius: "50%",
                  background: "none", color: "rgba(255,255,255,0.8)", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer",
                }}
              >
                <X size={13} strokeWidth={2.6} />
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * "Invite friends to play": the system share sheet with `text` (inviteText),
 * or the clipboard where there is none, saying so. `variant` "primary" is a
 * full width button for a light page; "onDark" is a text button for the navy
 * card.
 */
export function InviteButton({ text, variant = "primary", style }) {
  const [note, setNote] = useState(null);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const onClick = async () => {
    const result = await shareText(text, { title: "Pick Three" });
    if (result === "copied" || result === "failed") {
      setNote(result);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setNote(null), 2500);
    }
  };

  const label = note === "copied" ? "Link copied" : note === "failed" ? "Couldn't share" : "Invite friends to play";
  const look = variant === "onDark"
    ? { display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", marginTop: 8, padding: "8px 0", border: "none", background: "none", color: "#7dffa3", fontSize: 14, fontWeight: 700, cursor: "pointer" }
    : { ...PRIMARY_BUTTON, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 };
  return (
    <button type="button" onClick={onClick} style={{ ...look, ...style }}>
      <Share size={16} aria-hidden="true" />
      {label}
    </button>
  );
}
